// Duration display helpers shared across mobile + web loggers.

/**
 * Minutes between a session's start and end ISO timestamps, as RAW fractional
 * minutes (never rounded — callers round/format as they need). Returns null
 * when the session has no `endedAt` yet, either timestamp is unparseable, or
 * the end is not strictly after the start. The one shared source for "how long
 * was this closed session", replacing the inline `(endMs-startMs)/60_000` copy
 * that lived in sessionResult (density math, wants the raw value) and
 * workoutsList (its label is built by formatMinutesLabel). Distinct from the live
 * elapsed-so-far seconds the logger ticks while a session is still open —
 * that one needs a `now()` fallback and stays where it is.
 */
export function sessionDurationMin(
  startedAt: string,
  endedAt: string | undefined,
): number | null {
  if (!endedAt) return null;
  const startMs = new Date(startedAt).getTime();
  const endMs = new Date(endedAt).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return null;
  }
  return (endMs - startMs) / 60_000;
}

/**
 * THE duration of a session's work (#524): from the FIRST completed set to
 * `endedAt`, not from the draft's birth (`startedAt` stays the birth, for
 * ordering and the day). `completedAts` are the sets' completion stamps; one
 * before `startedAt` or unparseable is ignored (never lengthens the span).
 * No usable stamp, or the first one at/after `endedAt`: falls back to
 * sessionDurationMin, so a session with no completed set gets no invented span.
 */
export function sessionWorkDurationMin(
  startedAt: string,
  endedAt: string | undefined,
  completedAts: ReadonlyArray<string | null | undefined>,
): number | null {
  const full = sessionDurationMin(startedAt, endedAt);
  if (full === null) return null;
  const startMs = new Date(startedAt).getTime();
  const endMs = new Date(endedAt as string).getTime();
  let firstMs = Infinity;
  for (const c of completedAts) {
    if (!c) continue;
    const ms = new Date(c).getTime();
    if (Number.isFinite(ms) && ms > startMs && ms < firstMs) firstMs = ms;
  }
  if (!Number.isFinite(firstMs) || firstMs >= endMs) return full;
  // At least 1 min: a session with a completed set never reads "0 min".
  return Math.max(1, (endMs - firstMs) / 60_000);
}

/** The unit words of a duration label, already translated (t() at the call site). */
export type DurationUnits = { hour: string; minute: string };

/**
 * THE "1 h 05 min" duration label (whole minutes, rounded): the unit words come
 * from i18n, never hard-coded. Under an hour `45 min`; from an hour up the
 * minutes are zero-padded. `empty` is what a missing or non-positive span
 * reads as (callers differ: "" on the share card, an em-dash in the app).
 * Clock-style countdowns (mm:ss) are a different thing and stay numeric.
 */
export function formatMinutesLabel(
  minutes: number | null,
  units: DurationUnits,
  empty = '\u2014',
): string {
  if (minutes === null || !Number.isFinite(minutes) || minutes <= 0) return empty;
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} ${units.minute}`;
  return `${h} ${units.hour} ${String(m).padStart(2, '0')} ${units.minute}`;
}

/**
 * Format a duration in seconds as `mm:ss` (under one hour) or `h:mm:ss`
 * (one hour or more). Non-finite or non-positive inputs render as `0:00`.
 * Used by the cardio set row on both surfaces so a synced household reads
 * the same value.
 */
export function formatCardioDuration(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec <= 0) return '0:00';
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(r).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * The same duration as it must read in a NARROW fixed slot — the effort
 * meter's action column, `METER_ROW_COLUMN` = 48pt (MX-5 D12, narrowed from 76
 * on 2026-07-22 when that width pushed the row out of the card). Under an hour
 * `m:ss`; from an hour up `1h02`, because "1:02:30" does not fit and at 130%
 * text scale neither does "32:10". Four characters, no ambiguity — the exact seconds stay
 * in the h:min:sec fields above, where they're read anyway. Sits beside
 * formatCardioDuration deliberately: same input, different width budget, one
 * place to look so a third copy never gets invented.
 */
export function formatSlotDuration(totalSec: number): string {
  if (!Number.isFinite(totalSec)) return '0:00';
  const total = Math.max(0, Math.floor(totalSec));
  if (total >= 3600) {
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return `${h}h${String(m).padStart(2, '0')}`;
  }
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Split a total-seconds duration into whole hours / minutes / seconds.
 * Powers the hours:minutes:seconds entry field (typing raw seconds for a
 * 45-minute run is impractical — Antenore, 2026-05-31). Non-finite or
 * non-positive inputs split to all-zero.
 */
export function splitCardioDuration(totalSec: number): {
  h: number;
  m: number;
  s: number;
} {
  if (!Number.isFinite(totalSec) || totalSec <= 0) return { h: 0, m: 0, s: 0 };
  const t = Math.floor(totalSec);
  return { h: Math.floor(t / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
}

/**
 * Recombine hours / minutes / seconds into total seconds. Each part is
 * floored and clamped at 0 (negatives / NaN count as 0), so minutes or
 * seconds entered above 59 still accumulate (90m → 5400s) — the field
 * never rejects a value, it normalises on the next render via splitCardioDuration.
 */
export function joinCardioDuration(h: number, m: number, s: number): number {
  const safe = (n: number) => (Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);
  return safe(h) * 3600 + safe(m) * 60 + safe(s);
}
